import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Dropdown, DropdownMenu, DropdownItem, DropdownToggle, Input } from "reactstrap";
import { packageStatus, packageStatusStyles, meliStatusLabels, meliStatusClass } from "../../utils/packageStatus";
import { iconList } from "../../utils/iconList";
import Divider from "../divider";
import { roleRoutes } from "../../utils/roles";
import useAuth from "../../hooks/useAuth";
import moment from "moment";
import { RelativeTime } from "../ui/RelativeTime";
import { CopyButton } from "../ui/CopyButton";
import "./packageCard.scss";

/**
 * Componente de Card para mostrar paquetes en mobile
 */
export default function PackageCard({
  packageData,
  onCheckboxChange,
  isChecked,
  onViewEvidence,
  onViewVoucher,
  onCancel,
  onEdit,
  completeData
}) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  
  const toggleDropdown = () => setDropdownOpen(!dropdownOpen);
  
  // Encontrar el paquete completo
  const fullPackage = completeData?.find(
    (f) => f.followingNumber === packageData?.correlative?.comparative
  );
  
  const isAdmin = user?.role === roleRoutes.admin;
  const isClient = user?.role === roleRoutes.client;
  const isHolding = user?.role === roleRoutes.holdingAdmin;

  // Verificar si el paquete tiene overSizedEvidence y no es vacío
  const hasOverSizedEvidence = fullPackage?.overSizedEvidence && 
    (fullPackage.overSizedEvidence.reason || 
     (fullPackage.overSizedEvidence.images && fullPackage.overSizedEvidence.images.length > 0));

  const oversizedCategory = fullPackage?.overSizedEvidence?.category || (hasOverSizedEvidence ? 'XL' : null);

  return (
    <div className="package-card">
      {/* Header con checkbox, correlativo, status y acciones */}
      <div className="package-card__header">
        <div className="package-card__header-left">
          <Input
            type="checkbox"
            className="package-card__checkbox"
            checked={isChecked}
            onChange={() => onCheckboxChange && onCheckboxChange(packageData)}
          />
          <span className="inline-flex items-center gap-0.5 min-w-0">
            <span 
              className="package-card__correlative"
              onClick={() => onViewEvidence && onViewEvidence(packageData)}
            >
              {packageData?.correlative?.comparative}
            </span>
            <CopyButton
              value={packageData?.correlative?.comparative}
              label={t('Copiar número de seguimiento')}
              copiedMessage={t('Número de seguimiento copiado')}
              className="shrink-0"
            />
          </span>
        </div>
        
        <div className="package-card__header-right">
          {/* Status Badge */}
          <div className={`status-pill ${packageStatusStyles[fullPackage?.status]}`}>
            {packageStatus[fullPackage?.status]}
          </div>

          {/* Badge de estado MercadoLibre */}
          {fullPackage?.type?.toUpperCase() === 'MERCADOLIBRE' &&
           fullPackage?.meliStatus?.status != null && (
            <div className={`meli-badge ${meliStatusClass[fullPackage.meliStatus.status]}`}>
              <img src={iconList.ml} className="meli-badge__logo dark-safe-img" alt="MercadoLibre" />
              {meliStatusLabels[fullPackage.meliStatus.status]}
            </div>
          )}
          
          <Dropdown isOpen={dropdownOpen} toggle={toggleDropdown} className="package-card__dropdown">
            <DropdownToggle tag="div" className="package-card__dropdown-toggle">
              <img src={iconList["actions"]} alt="actions" />
            </DropdownToggle>
          <DropdownMenu end>
            {isClient && (
              <>
                <DropdownItem
                  className="dropdown-font"
                  onClick={() => onViewVoucher && onViewVoucher(packageData)}
                >
                  <div className="flex items-center gap-2">
                    <img
                      className="dropdown-font__image"
                      alt="detail"
                      src={iconList.qrcode}
                    />
                    {t("Ver detalle")}
                  </div>
                </DropdownItem>
                <Divider />
                <DropdownItem
                  className="dropdown-font"
                  disabled={packageData?.status?.status !== 0 && packageData?.status?.status !== 100}
                  onClick={() => onCancel && onCancel(packageData)}
                >
                  <div className="flex items-center gap-2">
                    <img
                      className="dropdown-font__image"
                      alt="cancel"
                      src={iconList.close}
                    />
                    {t("Cancelar")}
                  </div>
                </DropdownItem>
              </>
            )}

            {isAdmin && (
              <>
                <DropdownItem
                  className="dropdown-font"
                  onClick={() => onViewVoucher && onViewVoucher(packageData)}
                >
                  <div className="flex items-center gap-2">
                    <img
                      className="dropdown-font__image"
                      alt="detail"
                      src={iconList.qrcode}
                    />
                    {t("Ver etiqueta")}
                  </div>
                </DropdownItem>
                <Divider />
                <DropdownItem
                  className="dropdown-font"
                  disabled={packageData?.status?.value === 4 || packageData?.status?.value === 5}
                  onClick={() => onEdit && onEdit(packageData)}
                >
                  <div className="flex items-center gap-2">
                    <img
                      className="dropdown-font__image"
                      alt="editar"
                      src={iconList.edit}
                    />
                    {t("Editar pedido")}
                  </div>
                </DropdownItem>
                <Divider />
                <DropdownItem
                  className="dropdown-font"
                  disabled={packageData?.status?.value !== 0}
                  onClick={() => onCancel && onCancel(packageData)}
                >
                  <div className="flex items-center gap-2">
                    <img
                      className="dropdown-font__image"
                      alt="cancel"
                      src={iconList.close}
                    />
                    {t("Cancelar")}
                  </div>
                </DropdownItem>
              </>
            )}
          </DropdownMenu>
        </Dropdown>
        </div>
      </div>
      
      {/* Pill de sobredimensionado - debajo del header en mobile */}
      {hasOverSizedEvidence && oversizedCategory && (
        <div className="package-card__oversized-pill">
          <div className="status-pill status-pill--oversized">
            {oversizedCategory}
          </div>
        </div>
      )}
      
      {/* Información del paquete - REORDENADO */}
      <div className="package-card__content">
        
        {/* 1. Vendedor (admin, holding) */}
        {packageData?.sale && (isAdmin || isHolding) && (
          <div className="package-card__field">
            <span className="package-card__label">{t("Comercio")}:</span>
            <span className="package-card__value">
              {typeof packageData.sale === 'object'
                ? packageData.sale.props?.children
                : packageData.sale}
            </span>
          </div>
        )}
        
        {/* 2. Repartidor (solo admin) */}
        {packageData?.deliveryMan && isAdmin && (
          <div className="package-card__field">
            <span className="package-card__label">{t("Repartidor")}:</span>
            <div className="package-card__delivery">
              {packageData.deliveryMan.icon && (
                <img
                  src={packageData.deliveryMan.icon || iconList["defaultAvatar"]}
                  alt="delivery"
                  className="package-card__delivery-icon dark-safe-img"
                />
              )}
              <span className="package-card__value">{packageData.deliveryMan.name}</span>
            </div>
          </div>
        )}
        
        {/* 3. Dirección */}
        {packageData?.destiny && (
          <div className="package-card__field">
            <span className="package-card__label">{t("Dirección")}:</span>
            <span className="package-card__value">
              {typeof packageData.destiny === 'object'
                ? packageData.destiny.props?.children?.map(child => child?.props?.children).join(', ')
                : packageData.destiny}
            </span>
          </div>
        )}
        
        {/* 4. Comentario (solo admin) */}
        {packageData?.comment && isAdmin && (
          <div className="package-card__field">
            <span className="package-card__label">{t("Comentario")}:</span>
            <span className="package-card__value">
              {typeof packageData.comment === 'object'
                ? packageData.comment.props?.children?.props?.children
                : packageData.comment}
            </span>
          </div>
        )}
        
        {/* Cliente */}
        {packageData?.receiver && (
          <div className="package-card__field">
            <span className="package-card__label">{t("Cliente")}:</span>
            <span className="package-card__value">
              {typeof packageData.receiver === 'object' 
                ? packageData.receiver.props?.children?.map(child => child?.props?.children).join(' ')
                : packageData.receiver}
            </span>
          </div>
        )}
        
        {/* Orden de venta */}
        {packageData?.orderId && (
          <div className="package-card__field">
            <span className="package-card__label">{t("Número de venta")}:</span>
            {/* __field es una columna flex: sin este envoltorio el botón se estiraba a todo el ancho. */}
            <span className="inline-flex items-center gap-0.5 min-w-0">
              <span className="package-card__value">
                {typeof packageData.orderId === 'object'
                  ? packageData.orderId.props?.children?.props?.children
                  : packageData.orderId}
              </span>
              {/* orderId puede ser un nodo JSX: se copia el order_id crudo del pedido completo */}
              <CopyButton
                value={fullPackage?.order_id ?? (typeof packageData.orderId === 'object' ? null : packageData.orderId)}
                label={t('Copiar número de venta')}
                copiedMessage={t('Número de venta copiado')}
                className="shrink-0"
              />
            </span>
          </div>
        )}
        
        {/* Fechas */}
        <div className="package-card__dates">
          {(fullPackage?.createdAt || packageData?.createdAt) && (
            <div className="package-card__date">
              <span className="package-card__date-label">{t("Creado")}:</span>
              <span className="package-card__date-value">
                {fullPackage?.createdAt 
                  ? moment(fullPackage.createdAt).format("DD/MM/YY HH:mm")
                  : (typeof packageData.createdAt === 'object'
                    ? packageData.createdAt.props?.children?.props?.children
                    : packageData.createdAt)}
              </span>
            </div>
          )}
          
          {(fullPackage?.updatedAt || packageData?.ingresed || packageData?.lastUpdate) && (
            <div className="package-card__date">
              <span className="package-card__date-label">{t("Actualizado")}:</span>
              <span className="package-card__date-value">
                {fullPackage?.updatedAt
                  ? <RelativeTime value={fullPackage.updatedAt} live t={t} />
                  : (typeof (packageData.ingresed || packageData.lastUpdate) === 'object'
                    ? (packageData.ingresed || packageData.lastUpdate).props?.children?.props?.children
                    : (packageData.ingresed || packageData.lastUpdate))}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

